import { useEffect, useState } from 'react';
import { APP_BUILD_ID, DEV_BUILD_ID, fetchIsNewerBuildAvailable } from '@/lib/appVersionCheck';

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;

interface UseNewVersionAvailableOptions {
  /** Defaults to true for production builds and false for the dev server. */
  enabled?: boolean;
  pollIntervalMs?: number;
  /** Override the network check (tests). Must be referentially stable. */
  check?: () => Promise<boolean>;
}

/**
 * Becomes true once a newer frontend build is detected on the server.
 *
 * Checks run on a fixed interval and whenever the tab regains visibility or
 * focus (throttled to once per minute). No check runs on mount — a freshly
 * loaded page is by definition on the latest build. Once a newer build has
 * been seen the hook stops polling; only a reload can clear the flag.
 */
export function useNewVersionAvailable({
  enabled = APP_BUILD_ID !== DEV_BUILD_ID,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  check = fetchIsNewerBuildAvailable,
}: UseNewVersionAvailableOptions = {}): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!enabled || available) return undefined;

    let cancelled = false;
    let inFlight = false;
    let lastCheckedAt = 0;

    const runCheck = async (): Promise<void> => {
      if (cancelled || inFlight || document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastCheckedAt < MIN_CHECK_GAP_MS) return;
      lastCheckedAt = now;
      inFlight = true;
      try {
        if ((await check()) && !cancelled) {
          setAvailable(true);
        }
      } finally {
        inFlight = false;
      }
    };

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        void runCheck();
      }
    };

    const timer = window.setInterval(() => void runCheck(), pollIntervalMs);
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [enabled, available, pollIntervalMs, check]);

  return available;
}
